/**
 * Reservation slot logic to match customer-frontend: business hours, holidays,
 * slot duration, and 1 hour before close. Used by Reservations and TableManagement.
 */

const DEFAULT_OPENING = '16:00'
const DEFAULT_CLOSING = '22:00'
const HOURS_BEFORE_CLOSE = 1

export type DaySchedule = { open: string; close: string; closed: boolean }

export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr || typeof timeStr !== 'string') return 0
  const parts = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!parts) return 0
  const h = Math.min(23, Math.max(0, parseInt(parts[1], 10)))
  const m = Math.min(59, Math.max(0, parseInt(parts[2], 10)))
  return h * 60 + m
}

/** Build time slots; last slot is at least 1 hour before close. Supports overnight. */
export function buildTimeSlots(
  durationMinutes: number,
  openingTime: string,
  closingTime: string
): string[] {
  const slots: string[] = []
  const startMin = parseTimeToMinutes(openingTime) || parseTimeToMinutes(DEFAULT_OPENING)
  const endMin = parseTimeToMinutes(closingTime) ?? parseTimeToMinutes(DEFAULT_CLOSING)
  const duration = Math.max(15, Math.min(60, durationMinutes))
  const cutoffMin = 60 * HOURS_BEFORE_CLOSE

  if (endMin > startMin) {
    const lastSlotMax = Math.max(startMin, endMin - cutoffMin)
    for (let totalMin = startMin; totalMin <= lastSlotMax; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  } else if (endMin < startMin) {
    const lastBeforeMidnight = 24 * 60 - cutoffMin
    for (let totalMin = startMin; totalMin <= lastBeforeMidnight; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
    const lastAfterMidnight = Math.max(0, endMin - cutoffMin)
    for (let totalMin = 0; totalMin <= lastAfterMidnight; totalMin += duration) {
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  }
  return slots
}

export function isOvernightSlot(slot: string, openMinutes: number): boolean {
  return parseTimeToMinutes(slot) < openMinutes
}

export function getScheduleForDay(
  schedule: Record<string, DaySchedule> | undefined,
  dayIndex: number
): DaySchedule {
  const key = String(dayIndex)
  const slot = schedule?.[key]
  if (slot && typeof slot === 'object')
    return { open: slot.open || DEFAULT_OPENING, close: slot.close || DEFAULT_CLOSING, closed: !!slot.closed }
  return { open: DEFAULT_OPENING, close: DEFAULT_CLOSING, closed: false }
}

export function dateToYYYYMMDD(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Dates from today through the next 14 days (15 total). */
export function getDateRange(): Date[] {
  const dates: Date[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  for (let i = 0; i < 15; i++) {
    const d = new Date(today)
    d.setDate(today.getDate() + i)
    dates.push(d)
  }
  return dates
}

/** Get the actual datetime for a slot on the selected date (adds one day for overnight slots). */
export function getSlotDateTime(selectedDate: Date, slot: string, daySchedule: DaySchedule): Date {
  const [h, m] = slot.split(':').map(Number)
  const d = new Date(selectedDate)
  const openMin = parseTimeToMinutes(daySchedule.open)
  if (isOvernightSlot(slot, openMin)) {
    d.setDate(d.getDate() + 1)
  }
  d.setHours(h, m, 0, 0)
  return d
}

/** Check if a date is closed (holiday or that weekday is closed). */
export function isDateClosed(
  date: Date,
  schedule: Record<string, DaySchedule> | undefined,
  holidays: string[]
): boolean {
  const yyyyMmDd = dateToYYYYMMDD(date)
  if (holidays.includes(yyyyMmDd)) return true
  const daySchedule = getScheduleForDay(schedule, date.getDay())
  return daySchedule.closed
}

/** True if slot is on today and before (now + minimumBookingHours). Use to disable "too soon" slots. */
export function isSlotTooSoon(slotDateTime: Date, minimumBookingHours: number): boolean {
  const hours = Number(minimumBookingHours)
  if (!(hours > 0)) return false
  const todayStr = dateToYYYYMMDD(new Date())
  if (dateToYYYYMMDD(slotDateTime) !== todayStr) return false
  const cutoff = new Date()
  cutoff.setHours(cutoff.getHours() + hours, cutoff.getMinutes(), 0, 0)
  return slotDateTime.getTime() < cutoff.getTime()
}
